import type { Deck } from '../types';
import { Clock, BookOpen, Star, Edit, Trash, Play } from 'lucide-react';

interface DeckListProps {
  decks: Deck[];
  onSelectDeck?: (deck: Deck) => void;
  onEditDeck?: (deck: Deck) => void;
  onDeleteDeck?: (deckId: string) => void;
  onStudyDeck?: (deck: Deck) => void;
  className?: string;
  emptyMessage?: string;
}

export const DeckList: React.FC<DeckListProps> = ({
  decks,
  onSelectDeck,
  onEditDeck,
  onDeleteDeck,
  onStudyDeck,
  className = '',
  emptyMessage = 'No decks found. Create your first deck!',
}) => {
  if (decks.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
          <BookOpen className="w-8 h-8 text-gray-400 dark:text-gray-500" />
        </div>
        <p className="text-gray-600 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  const getColorFromString = (str: string) => {
    const colors = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-teal-500',
    ];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const formatDate = (date?: string) => {
    if (!date) return 'Never';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${className}`}>
      {decks.map((deck) => (
        <div
          key={deck.id}
          onClick={() => onSelectDeck?.(deck)}
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden group cursor-pointer"
        >
          {/* Color bar */}
          <div className={`h-2 w-full ${getColorFromString(deck.color || deck.id)}`} />

          <div className="p-4 space-y-3">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {deck.name}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">
                  {deck.description || 'No description'}
                </p>
              </div>
              {deck.isFavorite && (
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 shrink-0 ml-2" />
              )}
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <BookOpen className="w-3.5 h-3.5" />
                <span>{deck.cards.length} cards</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                <span>{formatDate(deck.lastStudied)}</span>
              </div>
            </div>

            {/* Tags */}
            {deck.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {deck.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              {onStudyDeck && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStudyDeck(deck);
                  }}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                >
                  <Play className="w-3.5 h-3.5" />
                  Study
                </button>
              )}
              {onEditDeck && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditDeck(deck);
                  }}
                  className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
              )}
              {onDeleteDeck && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteDeck(deck.id);
                  }}
                  className="p-1.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                >
                  <Trash className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DeckList;
